import CaretDownOutlined from '@ant-design/icons/CaretDownOutlined';
import { isMobile } from '@vindr/utils/mobile';
import { Button } from 'antd';
import { ItemType } from 'antd/es/menu/hooks/useItems';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import Dropdown from './Dropdown';
import Icon from './Icon';
import Tooltip from './Tooltip';

const getSplitButtonItems = (items) =>
  items.map((item, index) => ({
    ...item,
    key: item.id,
    index,
  }));

const SplitButton = ({ groupId, primary, secondary, items, onInteraction }) => {
  const { t } = useTranslation('ViewerTool');

  const handleSelectItem = (item) => {
    let foundItem;
    for (const it of items) {
      if (it?.id === item?.props?.id) {
        foundItem = it;
        break;
      } else if (it?.items) {
        foundItem = it?.items.find((i) => i?.id === item?.props?.id);
        if (foundItem) break;
      }
    }
    if (foundItem) {
      onInteraction({
        groupId,
        itemId: foundItem.id,
        commands: foundItem.commands,
      });
    } else {
      console.warn('Menu not found');
    }
  };

  const onPrimaryClickHandler = () => {
    onInteraction({
      groupId,
      itemId: primary.id,
      commands: primary.commands,
    });
  };

  const isPrimaryActive = primary.isActive;

  const menuItems: ItemType[] = getSplitButtonItems(items).map((it) => ({
    ...it,
    icon: <Icon name={it.icon} className="text-4xl" />,
    label: t(it.label, { keySeparator: '>', nsSeparator: '|' }),
    children: it?.items || null,
    className: 'text-white text-base h-8 hover:text-primary-light',
  }));

  return (
    <div className="relative mx-1.5 flex">
      <Tooltip title={!isMobile() ? t(primary.tooltip) : ''} placement="bottom">
        <Button
          type="link"
          onClick={onPrimaryClickHandler}
          className={`h-6 rounded-sm p-0 outline-none `}
        >
          <Icon
            name={primary.icon}
            className={`text-4xl hover:text-primary-light ${
              isPrimaryActive ? 'text-primary-light' : 'text-white'
            }`}
          />
        </Button>
      </Tooltip>
      <Dropdown
        menu={{
          items: menuItems,
          onClick: ({ item }) => {
            handleSelectItem(item);
          },
          className:
            'bg-background-light-3 dark:bg-background-dark-3 text-white hover:text-primary-light rounded-sm',
        }}
        trigger={['click']}
        className="align-center flex w-auto justify-center"
      >
        <Tooltip title={!isMobile() ? t(secondary.tooltip) : ''} placement="bottom">
          <Button type="link" className="flex h-6 items-center p-0">
            <CaretDownOutlined className="text-sm text-white hover:text-primary-light" />
          </Button>
        </Tooltip>
      </Dropdown>
    </div>
  );
};

SplitButton.defaultProps = {
  isRadio: false,
  isAction: false,
  primary: {
    label: null,
    tooltip: null,
  },
  secondary: {
    icon: 'chevron-down',
    tooltip: 'More Measure Tools',
  },
  items: [],
};

SplitButton.propTypes = {
  groupId: PropTypes.string,
  primary: PropTypes.shape({
    id: PropTypes.string.isRequired,
    icon: PropTypes.string,
    label: PropTypes.string,
    tooltip: PropTypes.string,
    isActive: PropTypes.bool,
    commands: PropTypes.array,
  }),
  secondary: PropTypes.shape({
    id: PropTypes.string,
    icon: PropTypes.string,
    tooltip: PropTypes.string,
  }),
  renderer: PropTypes.func,
  items: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      icon: PropTypes.string,
      label: PropTypes.string,
      tooltip: PropTypes.string,
      isActive: PropTypes.bool,
    })
  ),
  /** Callback function to inform ToolBarService of important events */
  onInteraction: PropTypes.func.isRequired,
};

export default SplitButton;
